// The row of dashes under the question header: one per question in the
// section, filled up to where the student is.
//
// It answers "how much of this is left" without the child having to read a
// fraction, which a single bar would do too - but the dashes also show how BIG
// the section is, so a 6-question section and a 30-question one do not look
// identical at the same percentage.
//
// Display-only, and capped: past MAX_SEGMENTS the dashes would be thinner than
// the gaps between them, so it falls back to a plain bar.

import React from "react"
import { BMD_UI } from "../../constants/diagnostics/bmd.theme"
import { BmdProgressBar } from "./BmdProgressBar"

const MAX_SEGMENTS = 20

export type BmdQuestionTrackerProps = {
  /** 1-based position of the question on screen. */
  current: number
  total: number
  className?: string
}

export const BmdQuestionTracker: React.FC<BmdQuestionTrackerProps> = ({
  current,
  total,
  className,
}) => {
  if (total > MAX_SEGMENTS) {
    return (
      <BmdProgressBar
        done={Math.max(0, current - 1)}
        total={total}
        height={5}
        className={className}
      />
    )
  }

  return (
    <div
      className={`d-flex gap-1 ${className ?? ""}`}
      aria-label={`Question ${current} of ${total}`}
    >
      {Array.from({ length: Math.max(1, total) }).map((_, i) => {
        // Three states, because "the one you are on" is worth telling apart
        // from "the ones you have done": answered dashes are solid, the current
        // one is tinted, and the rest are empty track.
        const isDone = i < current - 1
        const isCurrent = i === current - 1
        return (
          <span
            key={i}
            style={{
              flex: 1,
              height: 5,
              borderRadius: 3,
              backgroundColor: isDone
                ? BMD_UI.FILL
                : isCurrent
                ? BMD_UI.FILL_CURRENT
                : BMD_UI.TRACK,
            }}
          ></span>
        )
      })}
    </div>
  )
}

export default BmdQuestionTracker
